import { useRef, useEffect } from "react";
import type { UnifiedContactResponse } from "../../../types/crm";
import { pipelineStageLabel } from "../../../constants/pipelineStages";
import "./ContactsTable.css";

interface ContactsTableProps {
  contacts: UnifiedContactResponse[];
  loading: boolean;
  selectedIds: Set<string>;
  contactType: string;
  onSelectionChange: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onRowClick: (contact: UnifiedContactResponse) => void;
  openContactId: string | null;
}

const formatRelativeDate = (dateStr?: string): string => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
};

const NOTES_PREVIEW_LIMIT = 50;

const NotesCell = ({ notes }: { notes?: string }) => {
  if (!notes) return <span className="contacts-table__empty-cell">—</span>;
  const trimmed = notes.trim();
  if (trimmed.length <= NOTES_PREVIEW_LIMIT) return <span>{trimmed}</span>;
  const preview = trimmed.slice(0, NOTES_PREVIEW_LIMIT).trimEnd() + "…";
  return (
    <span className="contacts-table__notes" title={trimmed}>
      {preview}
    </span>
  );
};

export const ContactsTable = ({
  contacts,
  loading,
  selectedIds,
  contactType,
  onSelectionChange,
  onSelectAll,
  onRowClick,
  openContactId,
}: ContactsTableProps) => {
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const allSelected = contacts.length > 0 && contacts.every((c) => selectedIds.has(c.id));
  const someSelected = !allSelected && contacts.some((c) => selectedIds.has(c.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  if (loading) {
    return (
      <div className="contacts-table__loading">
        <div className="contacts-table__spinner" />
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="contacts-table__empty">
        No contacts found. Try adjusting your filters.
      </div>
    );
  }

  const isPlayers = contactType === "ProspectProfile";
  const showCheckboxes = contactType === "Coach";

  return (
    <div className="contacts-table-container">
    <table className="contacts-table">
      <thead className="contacts-table__header">
        <tr>
          {showCheckboxes && (
            <th className="contacts-table__th contacts-table__th--checkbox">
              <input
                ref={headerCheckboxRef}
                type="checkbox"
                checked={allSelected}
                onChange={(e) => onSelectAll(e.target.checked)}
                aria-label="Select all"
              />
            </th>
          )}
          <th className="contacts-table__th">Name</th>
          {isPlayers ? (
            <>
              <th className="contacts-table__th">Travel Team</th>
              <th className="contacts-table__th">Position</th>
              <th className="contacts-table__th">Class</th>
              <th className="contacts-table__th">Projected Division</th>
              <th className="contacts-table__th">Stage</th>
              <th className="contacts-table__th">State</th>
              <th className="contacts-table__th contacts-table__th--date">Last inbound</th>
              <th className="contacts-table__th">Notes</th>
            </>
          ) : (
            <>
              <th className="contacts-table__th">Team</th>
              <th className="contacts-table__th">Division</th>
              <th className="contacts-table__th">State</th>
              <th className="contacts-table__th contacts-table__th--date">Last inbound</th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {contacts.map((contact) => (
          <tr
            key={contact.id}
            className={`contacts-table__row ${openContactId === contact.id ? "contacts-table__row--active" : ""}`}
            onClick={() => onRowClick(contact)}
          >
            {showCheckboxes && (
              <td
                className="contacts-table__td contacts-table__td--checkbox"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(contact.id)}
                  onChange={(e) => onSelectionChange(contact.id, e.target.checked)}
                  aria-label={`Select ${contact.displayName}`}
                />
              </td>
            )}
            <td className="contacts-table__td contacts-table__td--name">
              {contact.displayName}
            </td>
            {isPlayers ? (
              <>
                <td className="contacts-table__td">
                  {contact.travelTeam ?? <span className="contacts-table__empty-cell">—</span>}
                </td>
                <td className="contacts-table__td">
                  {contact.position ?? <span className="contacts-table__empty-cell">—</span>}
                </td>
                <td className="contacts-table__td">
                  {contact.gradYear ?? <span className="contacts-table__empty-cell">—</span>}
                </td>
                <td className="contacts-table__td">
                  {contact.projectedDivisions && contact.projectedDivisions.length > 0 ? (
                    <span className="contacts-table__chip-group">
                      {contact.projectedDivisions.map((d) => (
                        <span key={d} className="contacts-table__chip">{d}</span>
                      ))}
                    </span>
                  ) : (
                    <span className="contacts-table__empty-cell">—</span>
                  )}
                </td>
                <td className="contacts-table__td">
                  {contact.pipelineStage ? (
                    pipelineStageLabel(contact.pipelineStage)
                  ) : (
                    <span className="contacts-table__empty-cell">—</span>
                  )}
                </td>
                <td className="contacts-table__td contacts-table__td--state">
                  {contact.state ?? <span className="contacts-table__empty-cell">—</span>}
                </td>
                <td className="contacts-table__td contacts-table__td--date">
                  {formatRelativeDate(contact.lastMessageReceivedAt)}
                </td>
                <td className="contacts-table__td">
                  <NotesCell notes={contact.notes} />
                </td>
              </>
            ) : (
              <>
                <td className="contacts-table__td contacts-table__td--school">
                  {contact.crmTeamName ? (
                    <span>{contact.crmTeamName}</span>
                  ) : (
                    <span className="contacts-table__empty-cell">—</span>
                  )}
                </td>
                <td className="contacts-table__td contacts-table__td--division">
                  {contact.division ?? <span className="contacts-table__empty-cell">—</span>}
                </td>
                <td className="contacts-table__td contacts-table__td--state">
                  {contact.state ?? <span className="contacts-table__empty-cell">—</span>}
                </td>
                <td className="contacts-table__td contacts-table__td--date">
                  {formatRelativeDate(contact.lastMessageReceivedAt)}
                </td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
};
